import { Alert, Button, cn, Spinner } from '@hvac/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from '../../shared/pdf/pdf-loader.js';

/**
 * The document viewer.
 *
 * PDFs render through pdf.js; images render natively with CSS transforms.
 * Both share one control surface, because a bookkeeper flipping between a
 * scanned PDF and a phone photo should not have to relearn the toolbar.
 *
 * Keyboard first: arrows page, +/- zoom, r rotates, f fits, 0 resets. Someone
 * checking forty receipts against a statement will not reach for the mouse
 * forty times.
 */

export type ViewerFit = 'width' | 'page' | 'actual';

export interface DocumentViewerProps {
  readonly url: string;
  readonly mimeType: string;
  readonly filename: string;
}

export function DocumentViewer({ url, mimeType, filename }: DocumentViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fit, setFit] = useState<ViewerFit>('width');
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  const isPdf = mimeType === 'application/pdf';
  const isImage = mimeType.startsWith('image/');

  const zoomIn = useCallback(() => {
    setFit('actual');
    setZoom((current) => Math.min(current * 1.25, 8));
  }, []);
  const zoomOut = useCallback(() => {
    setFit('actual');
    setZoom((current) => Math.max(current / 1.25, 0.1));
  }, []);
  const rotate = useCallback(() => setRotation((current) => (current + 90) % 360), []);
  const reset = useCallback(() => {
    setZoom(1);
    setRotation(0);
    setFit('width');
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      // Never hijack a key from a field someone is typing in.
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
          if (isPdf) {
            event.preventDefault();
            setPage((current) => Math.min(current + 1, pageCount));
          }
          break;
        case 'ArrowLeft':
        case 'PageUp':
          if (isPdf) {
            event.preventDefault();
            setPage((current) => Math.max(current - 1, 1));
          }
          break;
        case '+':
        case '=':
          event.preventDefault();
          zoomIn();
          break;
        case '-':
          event.preventDefault();
          zoomOut();
          break;
        case 'r':
          rotate();
          break;
        case 'f':
          setFit((current) => (current === 'width' ? 'page' : 'width'));
          break;
        case '0':
          reset();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPdf, pageCount, zoomIn, zoomOut, rotate, reset]);

  return (
    <div className="flex h-full flex-col rounded-[var(--radius-surface)] border border-border bg-surface-sunken">
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-surface px-2 py-1.5">
        {isPdf ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              ‹
            </Button>
            <span className="tabular text-xs text-content-muted">
              {page} / {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((current) => Math.min(current + 1, pageCount))}
              disabled={page >= pageCount}
              aria-label="Next page"
            >
              ›
            </Button>
          </div>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={zoomOut} aria-label="Zoom out">
            −
          </Button>
          <span className="tabular w-12 text-center text-xs text-content-muted">
            {Math.round(zoom * 100)}%
          </span>
          <Button variant="ghost" size="sm" onClick={zoomIn} aria-label="Zoom in">
            +
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFit('width')}
            className={cn(fit === 'width' && 'bg-surface-sunken')}
          >
            Fit width
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFit('page')}
            className={cn(fit === 'page' && 'bg-surface-sunken')}
          >
            Fit page
          </Button>
          <Button variant="ghost" size="sm" onClick={rotate} aria-label="Rotate 90 degrees">
            ⟳
          </Button>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-auto p-4">
        {isPdf ? (
          <PdfCanvas
            url={url}
            page={page}
            zoom={zoom}
            rotation={rotation}
            fit={fit}
            container={containerRef}
            onLoaded={setPageCount}
          />
        ) : isImage ? (
          <div className="flex min-h-full items-center justify-center">
            <img
              src={url}
              alt={filename}
              style={{
                transform: `rotate(${rotation}deg) scale(${zoom})`,
                transformOrigin: 'center',
                maxWidth: fit === 'actual' ? 'none' : '100%',
                maxHeight: fit === 'page' ? '100%' : 'none',
              }}
              className="transition-transform duration-150"
            />
          </div>
        ) : (
          <Alert tone="info" title="No preview available">
            {filename} cannot be displayed in the browser. Download it to open it.
          </Alert>
        )}
      </div>

      <p className="border-t border-border bg-surface px-3 py-1.5 text-xs text-content-subtle">
        ← → pages · + − zoom · r rotate · f fit · 0 reset
      </p>
    </div>
  );
}

/**
 * Renders one PDF page to a canvas.
 *
 * Re-rendered rather than CSS-scaled on zoom: scaling a bitmap produces a
 * blurry page, and the whole point of looking at a receipt is reading the
 * total. Render tasks are cancelled on change so fast paging does not queue
 * a backlog of stale renders.
 */
function PdfCanvas({
  url,
  page,
  zoom,
  rotation,
  fit,
  container,
  onLoaded,
}: {
  url: string;
  page: number;
  zoom: number;
  rotation: number;
  fit: ViewerFit;
  container: React.RefObject<HTMLDivElement | null>;
  onLoaded: (pageCount: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const documentRef = useRef<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Annotated so TypeScript does not narrow to the literal `false`; the
    // cleanup closure below is what flips it.
    let cancelled = false as boolean;

    void (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { loadPdfDocument } = await import('../../shared/pdf/pdf-loader.js');
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Could not fetch the document (${response.status}).`);
        const document_ = await loadPdfDocument(await response.arrayBuffer());
        if (cancelled) {
          await document_.cleanup();
          return;
        }
        documentRef.current = document_;
        onLoaded(document_.numPages);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'The document could not be opened.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      void documentRef.current?.cleanup();
      documentRef.current = null;
    };
  }, [url, onLoaded]);

  useEffect(() => {
    const document_ = documentRef.current;
    const canvas = canvasRef.current;
    if (document_ === null || canvas === null || isLoading) return undefined;

    let renderTask: { cancel: () => void } | null = null;
    let cancelled = false as boolean;

    void (async () => {
      try {
        const pdfPage = await document_.getPage(page);
        if (cancelled) return;

        const base = pdfPage.getViewport({ scale: 1, rotation });
        const available = container.current;
        const scale =
          fit === 'width' && available !== null
            ? (available.clientWidth - 32) / base.width
            : fit === 'page' && available !== null
              ? Math.min(
                  (available.clientWidth - 32) / base.width,
                  (available.clientHeight - 32) / base.height,
                )
              : zoom;

        const viewport = pdfPage.getViewport({ scale: Math.max(scale, 0.1), rotation });
        // Render at device resolution so text is crisp on a retina display.
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.ceil(viewport.width * ratio);
        canvas.height = Math.ceil(viewport.height * ratio);
        canvas.style.width = `${Math.ceil(viewport.width)}px`;
        canvas.style.height = `${Math.ceil(viewport.height)}px`;

        const context = canvas.getContext('2d');
        if (context === null) return;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        const task = pdfPage.render({ canvas, canvasContext: context, viewport });
        renderTask = task;
        await task.promise;
      } catch (caught) {
        // A cancelled render is the expected outcome of paging quickly.
        if (
          !cancelled &&
          caught instanceof Error &&
          caught.name !== 'RenderingCancelledException'
        ) {
          setError(caught.message);
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [page, zoom, rotation, fit, isLoading, container]);

  if (error !== null) {
    return (
      <Alert tone="danger" title="Could not display this document">
        {error}
      </Alert>
    );
  }

  return (
    <div className="flex min-h-full items-start justify-center">
      {isLoading ? (
        <Spinner className="size-6 text-brand-600" label="Opening document" />
      ) : (
        <canvas ref={canvasRef} className="shadow-[var(--shadow-card)]" />
      )}
    </div>
  );
}
